<template>
    <ion-page>
        <ion-loading :is-open="busy" message="Loading, please wait"/>
        <standard-header :title="title" align="middle"/>
        <ion-content>
            <ion-list>
                <ion-item lines="none">
                    <ion-label class="ion-text-wrap" v-if="hasScannedItem">
                        <h2>{{ scannedItem.name }}</h2>
                        <p>{{ scannedItem.item_number }}</p>
                    </ion-label>
                    <ion-label v-else>
                        <h2>Scan or search for item</h2>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openItemScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button expand="block" color="primary" size="medium" @click="openItemPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <div class="ion-padding-horizontal">
                    <ion-button v-if="hasScannedItem" expand="block" fill="outline" color="dark" @click="$refs['product'].open()">Product Card</ion-button>
                    <product-card-modal ref="product" :item="scannedItem" />
                </div>
            </ion-list>
            <ion-list v-if="hasScannedItem">
                <ion-item>
                    <ion-label><h2>Locations</h2></ion-label>
                </ion-item>
                <ion-item lines="none" v-for="location in locations" :key="location.id" button detail @click="move(location)">
                    <ion-avatar slot="start" class="ion-justify-content-center ion-align-items-center">
                        {{ location.pivot.qty_total + location.pivot.b_qty_total }}
                    </ion-avatar>
                    <ion-label class="ion-text-wrap">
                        <h2>
                            {{ location.code }}
                            <ion-badge v-if="location.is_temporary">temp</ion-badge>
                            <ion-badge v-show="location.pivot.qty_reserved > 0" color="danger" size="small">
                                Rsvd (A) {{ location.pivot.qty_reserved }}
                            </ion-badge>
                            <ion-badge v-show="location.pivot.b_qty_reserved > 0" color="danger" size="small">
                                Rsvd (B) {{ location.pivot.b_qty_reserved }}
                            </ion-badge>
                        </h2>
                        <p>
                            <span v-show="location.pivot.qty_total">Ax{{ location.pivot.qty_total }} </span>
                            <span v-show="location.pivot.qty_total > 0 && location.pivot.b_qty_total > 0">&nbsp;</span>
                            <span v-show="location.pivot.b_qty_total">Bx{{ location.pivot.b_qty_total }} </span>
                        </p>
                    </ion-label>
                </ion-item>
            </ion-list>
        </ion-content>
    </ion-page>

</template>

<script>
import StandardHeader from "@/components/StandardHeader";
import {
    IonButton,
    IonContent, IonIcon,
    IonAvatar,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonBadge,
    IonLoading,
    alertController, modalController
} from "@ionic/vue";
import {checkmarkCircle, search, barcode, listOutline} from 'ionicons/icons';
import {Capacitor} from "@capacitor/core";
import ItemSearchModalHelper from "@/services/ItemSearchModalHelper";
import ProductCardModal from "@/components/Modals/ProductCardModal";
import MoveModal from "@/views/modules/warehouse/stack/MoveModal";
export default {
    name: "List",
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    components: {
        ProductCardModal,
        StandardHeader,
        IonLoading,
        IonContent, IonItem, IonLabel, IonList, IonPage, IonIcon, IonButton, IonAvatar, IonBadge
    },
    props: {
        type: String
    },
    computed: {
        title() {
            return this.type + ' Items';
        },
        locations() {
            return this.hasScannedItem?this.scannedItem.locations:[];
        },
        include() {
            return 'skus,locations,locations.pivot,image_urls'
        },
        hasSelectedLocation() {
            return this.location && this.location.id > 0;
        },
        hasScannedItem() {
            return this.scannedItem && this.scannedItem.id > 0;
        }
    },
    methods: {
        reset() {
            this.scannedItem = {};
            this.location = {};
        },
        refresh(item) {
            this.busy = true;
            this.$http
                .get(`/api/items/${item.id}`, {params: {
                    include: this.include,
                    include_deleted_item_locations: true,
                    show_empty_locations: true
                }})
                .then(response => {
                    this.scannedItem = response.data;
                }).catch(error => {
                this.$validationAlert(error);
            }).finally(() => {
                this.busy = false;
            });
        },
        move(location) {
            let label = 'Move Items';
            switch (this.type) {
                case 'pick':
                    label = 'Pick Items';
                    break;
                case 'return':
                    label = 'Return Items';
                    break;
                case 'move':
                    label = 'Move Items';
                    break;
            }

            const componentProps = {
                initMode: this.type,
                item: this.scannedItem,
                source: location,
                confirmLabel: label
            };

            modalController.create({
                component: MoveModal,
                componentProps
            }).then(modal => {
                modal.present();
                modal.onDidDismiss()
                    .then(response => {
                        if (response.data) {
                            this.refresh(response.data);
                        }
                    });
            });
        },
        async openItemScanner() {
            if (Capacitor.platform === 'web') {
                const alert = await alertController.create({
                    header: 'Enter Item #ID',
                    inputs: [{
                        type: 'number',
                        name: 'item_id'
                    }],
                    buttons: ['Ok']
                });
                alert.onDidDismiss().then(input => {
                    const id = input.data.values.item_id;
                    this.$http.get(`/api/items/${id}`)
                        .then(response => {
                            this.refresh(response.data);
                        });
                });
                alert.present();
                return;
            }
            this.$scanner.search().then(result => {
                this.scannedItem = {};
                if (result) {
                    this.refresh(result);
                }
            }).catch(error => {
                this.scannedItem = {};
                this.$toast.danger(error);
            });
        },
        openItemPicker() {
            ItemSearchModalHelper.open(this.include).then(item => {
                if (! (item.data && item.data.id)) {
                    return;
                }
                this.refresh(item.data);
            });
        }
    },
    data() {
        return {
            user: {},
            busy: false,
            location: {},
            scannedItem: {}
        }
    },
    setup() {
        return {
            checkmarkCircle, search, barcode, listOutline
        }
    }

}
</script>
<style scoped>
ion-badge {
    vertical-align: bottom;
}
ion-avatar {
    border-radius: 50%;
    background-color: var(--ion-color-primary);
    color: var(--ion-color-primary-contrast);
    font-weight: bold;
    display:flex;
}
</style>
